import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Checkbox } from "@/components/ui/checkbox";
import { LANGS } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Upload, Search, Send, ShieldCheck } from "lucide-react";
import { StaffDialog } from "@/components/StaffDialog";
import { InviteDialog } from "@/components/InviteDialog";
import { CityInput } from "@/components/CityInput";
import { SendAccessButton } from "@/components/SendAccessButton";
import { CopyAccessButton } from "@/components/CopyAccessButton";
import { ResellerFilters, matchResellerFilters } from "@/components/ResellerFilters";
import { AccessBadge, accessState, ACCESS_FILTERS, sendState, SEND_FILTERS } from "@/components/AccessBadge";
import { CaseUpper } from "lucide-react";
import { SortHead, sortRows } from "@/components/SortHead";
import { NamesDialog } from "@/components/NamesDialog";
import { ExtraPriceCountries } from "@/components/ExtraPriceCountries";
import { CopyResellerList } from "@/components/CopyResellerList";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneWarn, phoneIssue } from "@/components/PhoneWarn";


const RANGES = [{ key: "van", label: "Van chevaux" }, { key: "prosport", label: "Pro & Sport" }];
const EMPTY = { id: null, name: "", last_name: "", first_name: "", company: "", email: "", phone: "", address: "", city: "", postal: "", password: "", sectors: [], sales_manager: false, can_translate: false, lang: "", exclusive: false, extra_price_countries: [], dealer_type: "concessionnaire" };

const splitName = (r) => {
  if (r.last_name || r.first_name) return { last_name: r.last_name || "", first_name: r.first_name || "" };
  const words = (r.name || "").trim().split(/\s+/).filter(Boolean);
  const isUp = (w) => w.length > 1 && w === w.toUpperCase() && /[A-Z]/i.test(w);
  const last = words.filter(isUp);
  if (!last.length) return { last_name: words.slice(-1).join(" "), first_name: words.slice(0, -1).join(" ") };
  return { last_name: last.join(" "), first_name: words.filter((w) => !isUp(w)).join(" ") };
};

const toText = (sectors, rk) => sectors.filter((s) => s.range_key === rk).map((s) => (s.area ? `${s.country}-${s.area}` : s.country)).join(", ");
const fromText = (txt, rk) => txt.split(/[,;\n]+/).map((t) => t.trim()).filter(Boolean).map((t) => {
  const [country, ...rest] = t.split("-");
  return { range_key: rk, country: country.trim().toUpperCase(), area: rest.join("-").trim() };
});

const summary = (sectors) => {
  const by = {};
  for (const s of sectors || []) { by[s.country] = by[s.country] || new Set(); if (s.area) by[s.country].add(s.area); }
  return Object.entries(by).map(([c, areas]) => {
    const a = [...areas].sort();
    if (!a.length) return c;
    return a.length <= 4 ? `${c} : ${a.join(", ")}` : `${c} (${a.length} zones)`;
  }).join(" · ");
};

function ImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("password", password);
      const { data } = await api.post("/admin/resellers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} créé(s), ${data.updated} mis à jour${data.skipped.length ? `, ${data.skipped.length} ignoré(s) (comptes admin)` : ""}`);
      onOpenChange(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reseller-import-dialog">
        <DialogHeader><DialogTitle>Importer les revendeurs (Excel)</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Une feuille par gamme (« Equestre » = Van chevaux, « Pro & Sport »), les autres feuilles = toutes gammes.
          Colonnes : Pays, Département / Code postal, Société, Vendeur, Téléphone, Email, Adresse.
          Les revendeurs existants (même email) sont mis à jour sans changer leur mot de passe.
        </p>
        <div className="space-y-3">
          <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="reseller-import-file" />
          <div><Label>Mot de passe provisoire des nouveaux comptes</Label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reseller-import-password" className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={!file || password.length < 6 || busy} data-testid="reseller-import-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResellerDialog({ form, setForm, onSave, onSent }) {
  const isNew = !form.id;
  const field = (k, label, type = "text") => (
    <div><Label>{label}</Label><Input type={type} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`reseller-${k}`} className="mt-1" /></div>
  );
  const setSectors = (rk, txt) => setForm({ ...form, _txt: { ...(form._txt || {}), [rk]: txt } });
  return (
    <Dialog open={!!form.open} onOpenChange={(v) => setForm({ ...form, open: v })}>
      <DialogContent className="max-w-2xl max-h-[92dvh] overflow-y-auto" data-testid="reseller-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Nouveau revendeur" : "Modifier le revendeur"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field("last_name", "Nom")}
          {field("first_name", "Prénom")}
          {field("company", "Société")}
          {field("email", "Email", "email")}
          {field("phone", "Téléphone")}
          {phoneIssue(form.phone) && <div className="col-span-2 -mt-2"><PhoneWarn phone={form.phone} testid="reseller-phone-warn" /></div>}
          <div className="col-span-2">{field("address", "Adresse")}</div>
          {field("postal", "Code postal du lieu de stockage")}
          <div><Label>Commune du lieu de stockage (vide = déduite de l'adresse)</Label>
            <CityInput value={form.city || ""} onChange={(v) => setForm((f) => ({ ...f, city: v }))} postal={form.postal}
              countryCode={(form.sectors || [])[0]?.country || "FR"} testid="reseller-city" className="mt-1" /></div>
          <div className="col-span-2">{field("password", isNew ? "Mot de passe" : "Nouveau mot de passe (laisser vide pour ne pas changer)")}</div>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer" data-testid="reseller-sales-manager">
            <Checkbox checked={!!form.sales_manager} onCheckedChange={(v) => setForm({ ...form, sales_manager: !!v })} data-testid="reseller-sales-manager-check" />
            Manager <span className="text-xs text-muted-foreground">(voit tous les devis des vendeurs France et Suisse, peut accorder une remise revendeur)</span>
          </label>
          <div className="col-span-2 flex items-center gap-2 text-sm">
            <span>Statut</span>
            <select value={form.dealer_type || "concessionnaire"} onChange={(e) => setForm({ ...form, dealer_type: e.target.value })} data-testid="reseller-dealer-type" className="h-8 rounded-md border border-input bg-background px-2 text-sm">
              <option value="concessionnaire">Concessionnaire (commande stock au prix revendeur)</option>
              <option value="agent">Agent (véhicule de démo mis à disposition)</option>
            </select>
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer" data-testid="reseller-exclusive">
            <Checkbox checked={!!form.exclusive} onCheckedChange={(v) => setForm({ ...form, exclusive: !!v })} data-testid="reseller-exclusive-check" />
            Revendeur exclusif Fautras <span className="text-xs text-muted-foreground">(décoché = vend aussi d'autres marques)</span>
          </label>
          {!form.sales_manager && (
            <ExtraPriceCountries value={form.extra_price_countries || []} onChange={(v) => setForm({ ...form, extra_price_countries: v })}
              sectorCountries={fromText(form._txt?.van ?? toText(form.sectors || [], "van"), "van").map((s) => s.country)} />
          )}
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer" data-testid="reseller-can-translate">
            <Checkbox checked={!!form.can_translate} onCheckedChange={(v) => setForm({ ...form, can_translate: !!v })} data-testid="reseller-can-translate-check" />
            Peut corriger les traductions
          </label>
          <div className="col-span-2 flex items-center gap-2 text-sm">
            <span>Langue par défaut</span>
            <select value={form.lang || ""} onChange={(e) => setForm({ ...form, lang: e.target.value })} data-testid="reseller-lang" className="h-8 min-w-0 flex-1 sm:flex-none rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Automatique (selon le pays du secteur){form.auto_lang ? ` — ${(LANGS.find(([c]) => c === form.auto_lang) || ["", form.auto_lang])[1]}` : ""}</option>
              {LANGS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
          </div>
          {RANGES.map((r) => (
            <div key={r.key} className="col-span-2">
              <Label>Secteur {r.label} <span className="text-xs text-muted-foreground font-normal">(ex. « FR-23, FR-77, ES, DE-8 » : pays seul = pays entier)</span></Label>
              <Input value={form._txt?.[r.key] ?? toText(form.sectors, r.key)} onChange={(e) => setSectors(r.key, e.target.value)} data-testid={`reseller-sectors-${r.key}`} className="mt-1 font-mono text-xs" />
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          {!isNew && <SendAccessButton r={form} onSent={onSent} label />}
          {!isNew && <CopyAccessButton r={form} onCopied={onSent} label />}
          <Button variant="outline" onClick={() => setForm({ ...form, open: false })}>Annuler</Button>
          <Button onClick={onSave} data-testid="reseller-save">{isNew ? "Créer" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResellersTab() {
  const [list, setList] = useState([]);
  const [staffOpen, setStaffOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [q, setQ] = useState("");
  const [access, setAccess] = useState("all");
  const [sendSt, setSendSt] = useState("all");
  const [namesOpen, setNamesOpen] = useState(false);
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [flt, setFlt] = useState({ country: "all", range: "all", excl: "all" });

  const load = () => { setLoading(true); api.get("/admin/resellers").then(({ data }) => setList(data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    const sectors = RANGES.flatMap((r) => fromText(form._txt?.[r.key] ?? toText(form.sectors, r.key), r.key));
    const ln = (form.last_name || "").trim().toUpperCase();
    const fn = (form.first_name || "").trim();
    if (!ln && !fn) { toast.error("Indiquez le nom et le prénom du vendeur"); return; }
    const body = { name: [ln, fn].filter(Boolean).join(" "), last_name: ln, first_name: fn, company: form.company, phone: form.phone, address: form.address, city: form.city || "", postal: form.postal || "", sectors, password: form.password, sales_manager: !!form.sales_manager, can_translate: !!form.can_translate, lang: form.lang || "", exclusive: !!form.exclusive, extra_price_countries: form.extra_price_countries || [], dealer_type: form.dealer_type || "concessionnaire" };
    try {
      if (form.id) {
        const { data } = await api.put(`/admin/resellers/${form.id}`, { ...body, email: form.email });
        if (data.email_changed) toast.info("Email modifié : pensez à renvoyer l'invitation à la nouvelle adresse");
      }
      else await api.post("/admin/resellers", { ...body, email: form.email });
      toast.success(form.id ? "Revendeur mis à jour" : "Revendeur créé");
      setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const markSent = (id, at) => setList((xs) => xs.map((x) => (x.id === id ? { ...x, ...(at ? { invited_at: at, invite_status: "sent" } : { invite_status: "failed" }) } : x)));
  const toggleExclusive = async (r, v) => {
    setList((xs) => xs.map((x) => (x.id === r.id ? { ...x, exclusive: v } : x)));
    try { await api.put(`/admin/resellers/${r.id}/exclusive`, { exclusive: v }); toast.success(`${r.name} : ${v ? "exclusif" : "non exclusif"}`); }
    catch (e) { setList((xs) => xs.map((x) => (x.id === r.id ? { ...x, exclusive: !v } : x))); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/resellers/${id}`); setList((xs) => xs.filter((x) => x.id !== id)); toast.success("Supprimé"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const shown = useMemo(() => list.filter((r) => (!q || [r.name, r.company, r.email, summary(r.sectors)].join(" ").toLowerCase().includes(q.toLowerCase()))
    && (access === "all" || accessState(r).key === access) && matchResellerFilters(r, flt) && (sendSt === "all" || sendState(r) === sendSt))
  , [list, q, access, flt, sendSt]);
  const sorted = useMemo(() => sortRows(shown, sort, {
    name: (r) => splitName(r).last_name || r.company || r.email, company: (r) => r.company || "", email: (r) => r.email || "",
    ranges: (r) => [...new Set((r.sectors || []).map((s) => RANGES.find((x) => x.key === s.range_key)?.label || s.range_key))].sort().join(" "),
    sector: (r) => summary(r.sectors) || "", access: (r) => accessState(r).key || "", exclusive: (r) => (r.exclusive ? 0 : 1),
  }), [shown, sort]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input placeholder="Rechercher (nom, société, email, pays, département)…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="reseller-search" />
        </div>
        <Select value={access} onValueChange={setAccess}>
          <SelectTrigger className="w-[260px]" data-testid="access-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ACCESS_FILTERS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`access-filter-${k}`}>{l} ({k === "all" ? list.length : list.filter((r) => accessState(r).key === k).length})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sendSt} onValueChange={setSendSt}>
          <SelectTrigger className="w-[220px]" data-testid="send-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SEND_FILTERS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`send-status-filter-${k}`}>{l} ({k === "all" ? list.length : list.filter((r) => sendState(r) === k).length})</SelectItem>)}
          </SelectContent>
        </Select>
        <ResellerFilters list={list} f={flt} setF={setFlt} />
        <span className="text-xs text-muted-foreground" data-testid="reseller-count">{shown.length} revendeur(s)</span>
        <Button variant="outline" className="gap-1" onClick={() => setInviteOpen(true)} data-testid="reseller-invite"><Send className="w-4 h-4" /> Envoyer les accès</Button>
        <Button variant="outline" className="gap-1" onClick={() => setStaffOpen(true)} data-testid="staff-open"><ShieldCheck className="w-4 h-4" /> Admins & managers</Button>
        <StaffDialog open={staffOpen} onOpenChange={setStaffOpen} onDone={load} />
        <InviteDialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) load(); }} />
        <Button variant="outline" className="gap-1" onClick={() => setNamesOpen(true)} data-testid="names-open"><CaseUpper className="w-4 h-4" /> Harmoniser les noms</Button>
        <NamesDialog open={namesOpen} onOpenChange={setNamesOpen} list={list} splitName={splitName} onDone={load} />
        <Button variant="outline" className="gap-1" onClick={() => setImportOpen(true)} data-testid="reseller-import"><Upload className="w-4 h-4" /> Importer Excel</Button>
        <CopyResellerList rows={shown} />
        <Button className="gap-1" onClick={() => setForm({ ...EMPTY, open: true })} data-testid="add-reseller"><Plus className="w-4 h-4" /> Nouveau revendeur</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto max-h-[65vh] overflow-y-auto">
          <Table>
            <TableHeader><TableRow>
              <SortHead k="name" sort={sort} setSort={setSort}>Vendeur</SortHead><SortHead k="company" sort={sort} setSort={setSort}>Société</SortHead><SortHead k="email" sort={sort} setSort={setSort}>Email</SortHead>
              <SortHead k="ranges" sort={sort} setSort={setSort}>Gammes</SortHead><SortHead k="sector" sort={sort} setSort={setSort}>Secteur</SortHead><SortHead k="access" sort={sort} setSort={setSort}>Accès</SortHead><SortHead k="exclusive" sort={sort} setSort={setSort} className="text-center">Exclusif</SortHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sorted.map((r) => {
                const rks = [...new Set((r.sectors || []).map((s) => s.range_key))];
                return (
                  <TableRow key={r.id} data-testid={`reseller-row-${r.email}`}>
                    <TableCell className="text-sm">{r.name}{r.admin_badge && <Badge className="ml-2 text-[10px] bg-[#1f2a44] hover:bg-[#1f2a44]" data-testid={`admin-badge-${r.email}`} title={r.fallback_owner ? "Responsable aussi des pays sans revendeur" : ""}>Admin</Badge>}{r.sales_manager && <Badge className="ml-2 text-[10px]" data-testid={`sales-manager-badge-${r.email}`}>Manager</Badge>}{r.exclusive && <Badge variant="outline" className="ml-2 text-[10px]" data-testid={`exclusive-badge-${r.email}`}>Exclusif</Badge>}{r.dealer_type === "agent" && <Badge variant="secondary" className="ml-2 text-[10px]" data-testid={`agent-badge-${r.email}`}>Agent</Badge>}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.company || "—"}</TableCell>
                    <TableCell className="text-xs">{r.email}</TableCell>
                    <TableCell className="space-x-1 whitespace-nowrap">{rks.length ? rks.map((k) => <Badge key={k} variant="secondary" className="text-[10px]">{RANGES.find((x) => x.key === k)?.label || k}</Badge>) : "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-xs">{summary(r.sectors) || "—"}</TableCell>
                    <TableCell className="max-w-[180px]"><AccessBadge r={r} /></TableCell>
                    <TableCell className="text-center">
                      <Checkbox checked={!!r.exclusive} onCheckedChange={(v) => toggleExclusive(r, !!v)} aria-label="Revendeur exclusif Fautras" data-testid={`exclusive-toggle-${r.email}`} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <SendAccessButton r={r} onSent={markSent} />
                      <CopyAccessButton r={r} onCopied={markSent} />
                      <Button size="icon" variant="ghost" onClick={() => setForm({ ...EMPTY, ...r, ...splitName(r), password: "", open: true })} data-testid={`edit-reseller-${r.email}`}><Pencil className="w-4 h-4" /></Button>
                      <ConfirmDelete label="ce revendeur" name={`${r.name} (${r.company || r.email})`} onConfirm={() => remove(r.id)} testid={`del-reseller-${r.email}`}>
                        <Button size="icon" variant="ghost" className="text-destructive" data-testid={`del-reseller-${r.email}`}><Trash2 className="w-4 h-4" /></Button>
                      </ConfirmDelete>
                    </TableCell>
                  </TableRow>
                );
              })}
              {shown.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Aucun revendeur</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <ResellerDialog form={form} setForm={setForm} onSave={save} onSent={markSent} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
}
